import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { ToastmessageService } from 'src/app/toastmessage.service';
import { ConfirmationMessageDialogComponent } from '../confirmation-message-dialog/confirmation-message-dialog.component';
import { MessMenuScenarioApi, MessService } from '../mess.service';
import { TransliterateService } from '../transliterate.service';

export interface MenuScenarioTab {
  scenarioId: number;
  scenarioName: string;
  icon: string;
}

export interface MenuListItem {
  id: string;
  menuItemId: number;
  itemNameEn: string;
  itemNameTe: string;
}

@Component({
  selector: 'app-add-new-menu-item',
  templateUrl: './add-new-menu-item.component.html',
  styleUrls: ['./add-new-menu-item.component.scss'],
})
export class AddNewMenuItemComponent implements OnInit, OnDestroy {
  scenarioTabs: MenuScenarioTab[] = [];
  activeScenarioId: number | null = null;
  searchText = '';
  showAddMissingForm = false;
  newItemName = '';
  newItemNameTe = '';
  duplicateError = '';

  private itemsByScenario: Record<number, MenuListItem[]> = {};
  private menuTeluguByEnglish: Record<string, string> = {};
  private readonly englishInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private service: MessService,
    private translitService: TransliterateService,
    private spinner: NgxSpinnerService,
    private toast: ToastmessageService,
    private matDialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.setupTransliteration();
    this.loadMenuCard();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.englishInput$.complete();
  }

  get activeScenarioName(): string {
    return this.scenarioTabs.find((tab) => tab.scenarioId === this.activeScenarioId)?.scenarioName ?? '';
  }

  get availableItems(): MenuListItem[] {
    if (this.activeScenarioId == null) {
      return [];
    }
    const query = this.searchText.trim().toLowerCase();
    const items = this.itemsByScenario[this.activeScenarioId] ?? [];
    if (!query) {
      return items;
    }
    return items.filter(
      (item) =>
        item.itemNameEn.toLowerCase().includes(query) ||
        item.itemNameTe.toLowerCase().includes(query),
    );
  }

  selectScenario(scenarioId: number): void {
    this.activeScenarioId = scenarioId;
    this.searchText = '';
    this.cancelAddMissing();
  }

  toggleAddMissingForm(): void {
    this.showAddMissingForm = !this.showAddMissingForm;
    if (!this.showAddMissingForm) {
      this.resetMissingForm();
    }
  }

  cancelAddMissing(): void {
    this.showAddMissingForm = false;
    this.resetMissingForm();
  }

  onNewItemNameChange(): void {
    this.duplicateError = '';
    this.englishInput$.next(this.newItemName.trim());
  }

  confirmAddMissingItem(): void {
    const itemNameEn = this.newItemName.trim();
    this.duplicateError = '';

    if (!itemNameEn) {
      this.toast.showError('Please enter menu item name.');
      return;
    }
    if (this.activeScenarioId == null) {
      this.toast.showError('Please select a meal tab.');
      return;
    }

    if (this.isDuplicateItem(itemNameEn)) {
      this.duplicateError = `"${itemNameEn}" already exists in ${this.activeScenarioName}. Duplicate items are not allowed.`;
      return;
    }

    const confirmRef = this.matDialog.open(ConfirmationMessageDialogComponent, {
      width: '400px',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: true,
      data: {
        title: 'Add Menu Item',
        message: `Are you sure you want to add ${itemNameEn}?`,
        confirmLabel: 'Add',
        confirmResult: 'add',
      },
    });
    confirmRef.afterClosed().subscribe((result) => {
      if (result === 'add') {
        this.submitAddMissingItem();
      }
    });
  }

  private submitAddMissingItem(): void {
    const itemNameEn = this.newItemName.trim();
    const itemNameTe = this.newItemNameTe.trim();

    if (!itemNameEn || this.activeScenarioId == null) {
      return;
    }

    const payload = {
      scenarioId: this.activeScenarioId,
      itemNameEn,
      itemNameTe,
    };

    this.spinner.show();
    this.service.addMissingMenuItem(payload).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        this.toast.showSuccess(res?.message ?? 'Menu item added successfully.');
        this.cancelAddMissing();
        this.loadMenuCard(true);
      },
      error: (err: any) => {
        this.spinner.hide();
        const message = err?.error?.message ?? 'Failed to add menu item.';
        if (this.isDuplicateApiError(message)) {
          this.duplicateError = message;
        } else {
          this.toast.showError(message);
        }
      },
    });
  }

  close(): void {
    this.router.navigate(['../adminDashboard'], { relativeTo: this.route });
  }

  private setupTransliteration(): void {
    this.englishInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((text) => this.resolveTeluguText(text)),
        takeUntil(this.destroy$),
      )
      .subscribe((teluguText) => {
        this.newItemNameTe = teluguText;
      });
  }

  private resolveTeluguText(text: string) {
    const itemNameEn = text.trim();
    if (!itemNameEn) {
      return of('');
    }

    const fromMenu = this.menuTeluguByEnglish[itemNameEn.toUpperCase()];
    if (fromMenu) {
      return of(fromMenu);
    }

    const words = itemNameEn.split(/\s+/).filter(Boolean);
    if (!words.length) {
      return of('');
    }

    return forkJoin(
      words.map((word) =>
        this.translitService.getSuggestions(word, 'te').pipe(
          map((suggestions) => suggestions[0] ?? ''),
          catchError(() => of('')),
        ),
      ),
    ).pipe(map((parts) => parts.filter(Boolean).join(' ')));
  }

  private resetMissingForm(): void {
    this.newItemName = '';
    this.newItemNameTe = '';
    this.duplicateError = '';
  }

  private rebuildMenuTeluguMap(): void {
    this.menuTeluguByEnglish = {};
    Object.values(this.itemsByScenario).forEach((items) => {
      items.forEach((item) => {
        const key = item.itemNameEn.trim().toUpperCase();
        if (key && item.itemNameTe) {
          this.menuTeluguByEnglish[key] = item.itemNameTe;
        }
      });
    });

    if (this.showAddMissingForm && this.newItemName.trim()) {
      this.englishInput$.next(this.newItemName.trim());
    }
  }

  private isDuplicateItem(itemNameEn: string): boolean {
    if (this.activeScenarioId == null) {
      return false;
    }
    const normalized = itemNameEn.trim().toLowerCase();
    return (this.itemsByScenario[this.activeScenarioId] ?? []).some(
      (item) => item.itemNameEn.trim().toLowerCase() === normalized,
    );
  }

  private isDuplicateApiError(message: string): boolean {
    const text = String(message ?? '').toLowerCase();
    return text.includes('duplicate') || text.includes('already exists') || text.includes('already present');
  }

  private loadMenuCard(skipSpinnerShow = false): void {
    if (!skipSpinnerShow) {
      this.spinner.show();
    }
    this.service.getMenuCard().subscribe({
      next: (res) => {
        const scenarios = Array.isArray(res) ? res : [];
        if (scenarios.length) {
          this.applyApiScenarios(scenarios);
        } else {
          this.scenarioTabs = [];
          this.itemsByScenario = {};
          this.menuTeluguByEnglish = {};
          this.activeScenarioId = null;
        }
        if (!skipSpinnerShow) {
          this.spinner.hide();
        }
      },
      error: () => {
        this.toast.showError('Failed to load menu items.');
        if (!skipSpinnerShow) {
          this.spinner.hide();
        }
      },
    });
  }

  private applyApiScenarios(scenarios: MessMenuScenarioApi[]): void {
    this.scenarioTabs = scenarios.map((scenario) => ({
      scenarioId: Number(scenario.scenarioId),
      scenarioName: String(scenario.scenarioName ?? '').trim(),
      icon: this.getScenarioIcon(String(scenario.scenarioName ?? '')),
    }));

    this.itemsByScenario = {};
    scenarios.forEach((scenario) => {
      const scenarioId = Number(scenario.scenarioId);
      this.itemsByScenario[scenarioId] = (scenario.messMenuItems ?? []).map((item, index) => ({
        id: `scenario-${scenarioId}-${item.menuItemId ?? index}`,
        menuItemId: Number(item.menuItemId),
        itemNameEn: String(item.itemNameEn ?? '').trim(),
        itemNameTe: String(item.itemNameTe ?? '').trim(),
      }));
    });

    if (this.activeScenarioId == null || !this.scenarioTabs.some((t) => t.scenarioId === this.activeScenarioId)) {
      this.activeScenarioId = this.scenarioTabs[0]?.scenarioId ?? null;
    }

    this.rebuildMenuTeluguMap();
  }

  private getScenarioIcon(scenarioName: string): string {
    const key = scenarioName.toLowerCase();
    if (key.includes('breakfast')) {
      return 'free_breakfast';
    }
    if (key.includes('lunch')) {
      return 'lunch_dining';
    }
    if (key.includes('snack')) {
      return 'bakery_dining';
    }
    if (key.includes('dinner')) {
      return 'dinner_dining';
    }
    return 'restaurant';
  }
}
